import React from "react";

const PaymentPolicy = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="bg-red-600 py-12 text-center text-white">
        <h1 className="text-3xl font-bold md:text-4xl">Payment Policy</h1>
        <p className="mt-3 text-sm md:text-base">For Customers Shopping on Havbit Marketplace</p>
        <div className="mt-4 text-xs md:text-sm opacity-90">
          <p>Last Updated: May 2026 | Effective Date: May 2026 | Version: 1.0</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 md:py-12">
        <div className="mb-10 rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-red-600">Table of Contents</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm md:text-base">
            <li>Payment Methods Accepted on Havbit</li>
            <li>Payment Security — How Your Money is Protected</li>
            <li>Cash on Delivery (COD) — Detailed Terms</li>
            <li>What Happens When a Payment Fails</li>
            <li>Pricing, Taxes, and Invoice</li>
            <li>Havbit Wallet</li>
            <li>Refunds — Process and Timeline</li>
            <li>Fraud Protection and Chargeback Policy</li>
            <li>Contact Us</li>
          </ol>
        </div>

        <div className="space-y-8 text-base leading-7 text-gray-800">
          <p>
            This Payment Policy explains in detail all aspects of payment on the Havbit marketplace
            — including the payment methods we accept, how your payments are secured, the Cash on
            Delivery process, what happens when a payment fails, how refunds are processed, and how
            we protect you from fraud. Please read this Policy before making a purchase on the
            platform.
          </p>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">1. Payment Methods Accepted on Havbit</h2>
            <p>
              Havbit accepts a wide range of payment methods to ensure you can always complete your
              purchase conveniently and securely.
            </p>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Payment Method</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">How It Works</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Availability</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">UPI</td>
                    <td className="border border-gray-200 px-3 py-2">Pay directly from your bank account using any UPI app — Google Pay, PhonePe, Paytm, BHIM, Amazon Pay, or your bank's UPI app. Enter your UPI ID or scan the QR code at checkout.</td>
                    <td className="border border-gray-200 px-3 py-2">All orders — all pin codes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Net Banking</td>
                    <td className="border border-gray-200 px-3 py-2">Pay directly through your bank's internet banking portal. All major Indian banks are supported including SBI, HDFC, ICICI, Axis, Kotak, Bank of Baroda, and PNB.</td>
                    <td className="border border-gray-200 px-3 py-2">All orders — all pin codes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Credit Card</td>
                    <td className="border border-gray-200 px-3 py-2">Pay using Visa, Mastercard, RuPay Credit, or American Express credit cards. 3D Secure / OTP verification required for all card transactions.</td>
                    <td className="border border-gray-200 px-3 py-2">All orders — all pin codes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Debit Card</td>
                    <td className="border border-gray-200 px-3 py-2">Pay using Visa, Mastercard, Maestro, or RuPay debit cards linked to your bank account. OTP verification required.</td>
                    <td className="border border-gray-200 px-3 py-2">All orders — all pin codes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Havbit Wallet</td>
                    <td className="border border-gray-200 px-3 py-2">Use the prepaid wallet balance in your Havbit account. Can be topped up from your bank or loaded from refunds and promotional credits.</td>
                    <td className="border border-gray-200 px-3 py-2">All orders — all pin codes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Cash on Delivery (COD)</td>
                    <td className="border border-gray-200 px-3 py-2">Pay in cash to the delivery person when your order arrives. No online payment required.</td>
                    <td className="border border-gray-200 px-3 py-2">Select pin codes — availability shown at checkout</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">No-Cost EMI</td>
                    <td className="border border-gray-200 px-3 py-2">Split your payment into easy monthly instalments on eligible credit cards from partner banks.</td>
                    <td className="border border-gray-200 px-3 py-2">Eligible orders — participating bank cards only</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">2. Payment Security — How Your Money is Protected</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">2.1 PCI-DSS Compliance</h3>
            <p>
              All payment transactions on the Havbit platform are processed through payment gateway
              partners certified to PCI-DSS (Payment Card Industry Data Security Standard) Level 1 —
              the highest level of payment security certification in the global payments industry. This
              certification requires our payment partners to maintain the most stringent data security
              controls for handling, processing, and storing payment card data.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.2 Havbit Does Not Store Your Payment Credentials</h3>
            <p>
              Havbit's own servers do not receive, store, or have access to your payment credentials —
              including your credit card number, debit card number, CVV code, UPI PIN, or net banking
              password. When you enter your payment details at checkout, this information is transmitted
              directly and securely to our payment gateway partner's encrypted servers. Havbit receives
              only a tokenised transaction reference confirming that your payment was successful.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.3 3D Secure and OTP Verification</h3>
            <p>
              All credit and debit card transactions on Havbit require 3D Secure authentication — you
              will receive an OTP (One-Time Password) on your registered mobile number from your bank,
              which you must enter to complete the payment. This ensures that even if your card details
              are compromised, a transaction cannot be completed without the OTP sent to your phone.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.4 Protect Yourself — What Havbit Will Never Ask</h3>
            <p className="font-medium text-red-600">
              IMPORTANT — Fraud Prevention: Havbit will NEVER ask you for your card number, CVV, UPI
              PIN, net banking password, or OTP through any channel — including phone calls,
              WhatsApp messages, emails, or social media. If anyone contacts you claiming to be from
              Havbit and asks for this information, it is a fraud attempt. Do not share your details and
              report the incident to hello@havbit.in and to the National Cyber Crime Helpline at 1930.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">3. Cash on Delivery (COD) — Detailed Terms</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">3.1 What Is COD and How Does It Work?</h3>
            <p>
              Cash on Delivery (COD) allows you to pay for your order in cash when it is delivered to
              your address, without making any online payment at the time of ordering. COD is available
              at select delivery pin codes where our courier partners support cash collection. The
              availability of COD at your pin code will be displayed at the checkout stage.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">3.2 Rules for COD Payments</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>Pay the exact order amount as shown on your order confirmation — the delivery person does not carry change for round-off amounts.</li>
              <li>Payment must be made in Indian Rupees in cash only — no digital payments, cheques, or cards are accepted for COD orders at the door.</li>
              <li>The delivery person will issue you a payment receipt upon collection — always take the receipt.</li>
              <li>Do not pay COD to anyone other than the uniformed delivery executive from our courier partner — if in doubt about the delivery person's identity, call our customer support before paying.</li>
              <li>If you refuse to pay COD without a valid reason, the order will be returned and you may be charged forward and return shipping costs.</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">3.3 COD Order Value Limits</h3>
            <p>
              COD is available for orders up to a maximum value that is displayed at checkout. Orders
              above this value require online payment. Havbit periodically revises the maximum COD
              value based on operational requirements.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">3.4 COD and Your Account</h3>
            <p>
              Repeated non-payment or refusal of COD orders may result in the COD payment option being
              disabled for your account. If you consistently refuse COD deliveries without valid reason,
              Havbit reserves the right to restrict your use of COD for future orders.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">4. What Happens When a Payment Fails</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">4.1 Order Not Confirmed After Payment Attempt</h3>
            <p>
              If your payment attempt fails at checkout — due to insufficient balance, network interruption,
              bank server downtime, incorrect card details, or OTP timeout — your order will not be
              confirmed and will not be sent to the seller. You may retry the payment using the same or a
              different payment method.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">4.2 Money Deducted But Order Not Confirmed</h3>
            <p>
              In some cases, your bank or UPI app may deduct the payment amount from your account even
              though Havbit's system did not receive a successful payment confirmation. This typically
              occurs due to a network disruption during the payment processing phase — the payment
              gateway received the debit instruction but could not confirm the response to Havbit's
              system.
            </p>

            <p className="mt-3">
              If this happens, please follow these steps:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Wait 15–30 minutes: In most cases, the payment will either be confirmed by the bank and the order will be placed automatically, or the bank will automatically reverse the deduction within a few hours.</li>
              <li>Check your bank statement: If the amount has been deducted but you have not received an order confirmation email within 30 minutes, check your bank statement or UPI history to confirm whether the deduction occurred.</li>
              <li>Contact Havbit: If the amount has been deducted and you have not received an order confirmation within 1 hour, email hello@havbit.in with your registered phone number or email, the approximate transaction time, the payment method used, and the last 4 digits of the card (if applicable). Our team will investigate with the payment gateway and resolve the issue within 2 business days.</li>
              <li>Auto-reversal: If the deduction is confirmed and the order is not placed, the amount will be automatically reversed to your bank account within 5–7 business days, depending on your bank's processing timeline.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">5. Pricing, Taxes, and Invoice</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">5.1 Pricing Policy</h3>
            <p>
              All prices displayed on the Havbit platform are in Indian Rupees (INR) and are inclusive of
              all applicable taxes — including GST — unless explicitly stated otherwise. The price you see
              at checkout is the final total you will pay. Havbit does not add any hidden charges after you
              confirm your order. All products on Havbit are sold at or below the Maximum Retail Price
              (MRP) printed on the product packaging, in compliance with the Legal Metrology Act, 2009.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">5.2 Shipping Charges</h3>
            <p>
              Shipping charges, where applicable, are calculated based on the total weight of your order
              and your delivery location, and are displayed separately at checkout before you confirm the
              order. If your order qualifies for free shipping, this will be displayed at checkout.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">5.3 Tax Invoice</h3>
            <p>
              A tax invoice will be generated by the seller for each order and will be made available for
              download through your Havbit account under 'My Orders' → select the order → 'Download
              Invoice', once your order is dispatched. This invoice can be used for GST input tax credit
              claims by GST-registered business purchasers, where applicable.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">6. Havbit Wallet</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">6.1 What Is the Havbit Wallet?</h3>
            <p>
              The Havbit Wallet is a prepaid payment instrument available within your Havbit account.
              Wallet balance can be used to pay for products on the Havbit platform, in full or in
              combination with another payment method. The Havbit Wallet is issued by Havbit Global
              Private Limited and is governed by applicable RBI guidelines for semi-closed prepaid
              payment instruments.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.2 How Wallet Balance Is Added</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>Refunds from returned or cancelled orders credited to your wallet (at your option)</li>
              <li>Promotional credits, cashback rewards, and referral bonuses</li>
              <li>Direct top-up from your bank account via UPI or net banking (where available)</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.3 Wallet Terms and Conditions</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>Wallet balance is non-transferable — it can only be used for purchases on the Havbit platform by the account holder.</li>
              <li>Wallet balance cannot be withdrawn to a bank account, except where it represents a refund amount due to you following a cancellation or return.</li>
              <li>Promotional wallet credits have a validity period that will be communicated at the time of credit. Unused promotional credits expire after the validity period.</li>
              <li>Havbit reserves the right to expire unused promotional credits after the stated validity period. Refund-origin wallet credits do not expire while your account is active.</li>
              <li>In the event of account closure, any refund-origin wallet balance will be transferred to your registered bank account within 30 days.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">7. Refunds — Process and Timeline</h2>
            <p>
              Refunds on Havbit are processed for the following reasons: order cancellation within the
              permitted window; seller cancellation; Havbit cancellation; validated return requests; and
              confirmed lost-in-transit orders. The refund process and timelines are as follows:
            </p>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Payment Method</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Timeline</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Mode</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">UPI</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credited to source UPI-linked bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Net Banking</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credited to source bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Credit Card</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Statement credit to the card used</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Debit Card</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credited to source bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Havbit Wallet (instant option)</td>
                    <td className="border border-gray-200 px-3 py-2">Within 24 hours</td>
                    <td className="border border-gray-200 px-3 py-2">Credited to Havbit Wallet</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Cash on Delivery</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">NEFT/IMPS to your registered bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">No-Cost EMI</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">EMI cancelled — card credit</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">8. Fraud Protection and Chargeback Policy</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">8.1 How We Protect You from Fraud</h3>
            <p>
              Havbit employs advanced fraud detection systems that monitor every transaction for signs
              of fraudulent activity — including unusual purchase patterns, mismatched device data, and
              high-risk payment indicators. Orders flagged as potentially fraudulent may be placed on hold
              for verification. You may receive a call or email from our team requesting identity
              verification for high-value or unusual orders.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">8.2 Reporting Unauthorised Transactions</h3>
            <p>
              If you notice a transaction on your bank statement or payment history that you did not
              authorise — and believe may be related to your Havbit account being compromised —
              please: immediately change your Havbit account password; contact your bank to report the
              unauthorised transaction and request a card block if necessary; and email
              hello@havbit.in with details of the unauthorised transaction including the amount, date,
              and order ID if available.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">8.3 Chargeback Policy</h3>
            <p>
              A chargeback is a dispute you raise with your bank or card issuer to reverse a transaction.
              Chargebacks are a legitimate remedy for genuinely unauthorised transactions. However,
              filing a chargeback for a legitimate purchase that you actually received — sometimes
              referred to as 'friendly fraud' — is a serious violation of Havbit's Terms of Use and may
              constitute fraud under applicable Indian law. If Havbit receives a chargeback on a
              legitimate transaction, we will provide the bank with all relevant transaction evidence.
              Accounts involved in fraudulent chargeback activity will be permanently suspended.
            </p>
          </section>

          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm leading-7 text-gray-700">
            <p className="font-semibold text-red-700">Havbit Global Private Limited</p>
            <p>CIN: U63120HR2026PTC141507</p>
            <p>Innov8 UCP, 9th Floor, Tower D, Unitech Cyber Park, Gurgaon – 122001, Haryana, India</p>
            <p>Customer Support Email: hello@havbit.in</p>
            <p>Website: www.havbit.in</p>
            <p>Support Hours: Monday – Saturday, 10:00 AM – 6:00 PM IST</p>
            <p className="mt-3">
              By using the Havbit platform — whether by browsing, creating an account, or placing an
              order — you confirm that you have read, understood, and agree to be bound by this
              Payment Policy and all related Havbit policies as updated from time to time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPolicy;
